import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Copy, Download, Link2, MessageCircle, Printer, Send } from 'lucide-react';

import { SendPdfModal } from '../../components/SendPdfModal';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';
import { openWhatsAppMessage } from '../../modules/legacy-core/utils/whatsapp';
import { api } from '../../services/api';
import { legacyApi, type Customer, type SaleOrder } from '../../services/legacyApi';
import type { AllocationLine, ArOpenInvoice, ArReceiptRecord } from '../../services/types';
import { toast } from 'sonner';

function formatReceiptNumber(receiptId?: number | null): string {
    const numeric = Number(receiptId || 0);
    return numeric > 0 ? `HC/RCPT/${String(numeric).padStart(4, '0')}` : '-';
}

function formatInvoiceNumber(invoiceId?: number | null): string {
    const numeric = Number(invoiceId || 0);
    return numeric > 0 ? `HC/INV/${String(numeric).padStart(4, '0')}` : '-';
}

function formatSoNumber(soId?: number | null): string {
    const numeric = Number(soId || 0);
    return numeric > 0 ? `HC/SO/${String(numeric).padStart(4, '0')}` : '-';
}

function formatAmount(amount?: number | null): string {
    return Number(amount || 0).toFixed(2);
}

function formatDate(value?: string | null): string {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString();
}

function receiptTitle(mode?: string): string {
    return String(mode || '').toUpperCase() === 'CUSTOMER_PREPAYMENT' ? 'Advance Payment Receipt' : 'Customer Receipt';
}

function receiptModeLabel(mode?: string): string {
    const normalized = String(mode || '').toUpperCase();
    if (normalized === 'CUSTOMER_PREPAYMENT') return 'Customer Prepayment';
    if (normalized === 'INVOICE_SETTLEMENT') return 'Invoice Settlement';
    if (normalized === 'LEGACY_UNAPPLIED') return 'Unapplied Receipt';
    return normalized || 'Receipt';
}

export function ReceiptDetail() {
    const { receiptId } = useParams<{ receiptId: string }>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { hasPermission } = useAuth();
    const numericReceiptId = Number(receiptId || 0);

    const [receipt, setReceipt] = useState<ArReceiptRecord | null>(null);
    const [customer, setCustomer] = useState<Customer | null>(null);
    const [saleOrder, setSaleOrder] = useState<SaleOrder | null>(null);
    const [shareUrl, setShareUrl] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [showSendPdfModal, setShowSendPdfModal] = useState(false);
    const [allocating, setAllocating] = useState(false);
    const [allocationLoading, setAllocationLoading] = useState(false);
    const [allocationSaving, setAllocationSaving] = useState(false);
    const [openInvoices, setOpenInvoices] = useState<ArOpenInvoice[]>([]);
    const [allocationDraft, setAllocationDraft] = useState<Record<number, string>>({});

    const canPrint = hasPermission('finance.receipt.pdf');
    const canEmail = hasPermission('finance.receipt.email');
    const canShare = hasPermission('finance.receipt.share');

    useEffect(() => {
        if (!numericReceiptId) {
            setLoading(false);
            return;
        }
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            try {
                const receiptData = await api.financials.getArReceipt(numericReceiptId);
                if (cancelled) return;
                setReceipt(receiptData);

                const customers = await legacyApi.customers.list();
                if (cancelled) return;
                const matchedCustomer = customers.find((row) => row.customer_id === Number(receiptData.customer_id)) || null;
                setCustomer(matchedCustomer);

                if (receiptData.so_id) {
                    try {
                        const so = await legacyApi.sales.getOrder(Number(receiptData.so_id));
                        if (!cancelled) setSaleOrder(so);
                    } catch (error) {
                        console.warn('Failed to load related sale order', error);
                    }
                } else if (!cancelled) {
                    setSaleOrder(null);
                }
            } catch (error: any) {
                console.error(error);
                if (!cancelled) toast.error(error?.message || 'Failed to load receipt');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [numericReceiptId]);

    const canAllocate = String(receipt?.mode || '').toUpperCase() === 'CUSTOMER_PREPAYMENT' && Number(receipt?.unapplied_amount || 0) > 0.01;

    useEffect(() => {
        if (!receipt || !canAllocate) return;
        if (searchParams.get('allocate') === '1') {
            void beginAllocation();
        }
    }, [receipt?.receipt_id, receipt?.unapplied_amount, receipt?.mode, searchParams.toString()]);

    const documentNumber = useMemo(() => formatReceiptNumber(receipt?.receipt_id), [receipt?.receipt_id]);
    const allocationTotal = useMemo(
        () => Number((receipt?.allocations || []).reduce((sum, line) => sum + Number(line.apply_amount || 0), 0).toFixed(2)),
        [receipt?.allocations]
    );
    const pendingAllocationLines = useMemo<AllocationLine[]>(
        () =>
            openInvoices
                .map((invoice) => {
                    const value = parseFloat(allocationDraft[invoice.invoice_id] ?? '');
                    if (!Number.isFinite(value) || value <= 0) return null;
                    return { document_id: invoice.invoice_id, apply_amount: Number(value.toFixed(2)) };
                })
                .filter((line): line is AllocationLine => !!line),
        [allocationDraft, openInvoices]
    );
    const pendingAllocationTotal = useMemo(
        () => Number(pendingAllocationLines.reduce((sum, line) => sum + line.apply_amount, 0).toFixed(2)),
        [pendingAllocationLines]
    );

    const handlePrint = () => {
        if (!receipt) return;
        try {
            api.financials.openArReceiptPrint(Number(receipt.receipt_id), false);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to open receipt print preview');
        }
    };

    const handleDownloadPdf = async () => {
        if (!receipt) return;
        try {
            await api.financials.downloadArReceiptPdf(Number(receipt.receipt_id));
        } catch (error: any) {
            toast.error(error?.message || 'Failed to download receipt PDF');
        }
    };

    const handleSendPdf = async (payload: { to: string; cc?: string; subject?: string; body?: string }) => {
        if (!receipt) return;
        const result = await api.financials.emailArReceiptPdf(Number(receipt.receipt_id), payload);
        toast.success(result.message || 'Receipt PDF emailed successfully');
    };

    const handleCreateShareLink = async (channel?: string) => {
        if (!receipt) return null;
        try {
            const result = await api.financials.createArReceiptShareLink(Number(receipt.receipt_id), channel ? { channel } : undefined);
            setShareUrl(result.share_url);
            return result;
        } catch (error: any) {
            toast.error(error?.message || 'Failed to create receipt share link');
            return null;
        }
    };

    const handleCopyShareLink = async () => {
        const result = await handleCreateShareLink('LINK');
        if (!result) return;
        try {
            await navigator.clipboard.writeText(result.share_url);
            toast.success(result.is_new ? 'Receipt share link created and copied.' : 'Receipt share link copied.');
        } catch {
            window.prompt('Copy receipt share link', result.share_url);
        }
    };

    const handleRevokeShareLink = async () => {
        if (!receipt) return;
        try {
            await api.financials.revokeArReceiptShareLink(Number(receipt.receipt_id));
            setShareUrl('');
            toast.success('Receipt share link revoked.');
        } catch (error: any) {
            toast.error(error?.message || 'Failed to revoke receipt share link');
        }
    };

    const handleShareWhatsApp = async () => {
        if (!receipt) return;
        const result = await handleCreateShareLink('WHATSAPP');
        if (!result) return;
        const message = `Please find receipt ${documentNumber}: ${result.share_url}`;
        const resolved = openWhatsAppMessage(message, customer);
        setShareUrl(result.share_url);
        if (!resolved.normalizedNumber) {
            toast.warning('No valid customer WhatsApp number found. Opened WhatsApp without a selected recipient.');
        }
    };

    const beginAllocation = async () => {
        if (!receipt) return;
        setAllocationLoading(true);
        try {
            const invoices = await api.financials.listArOpenInvoices(Number(receipt.customer_id));
            setOpenInvoices(invoices || []);
            setAllocating(true);
            let remaining = Number(receipt.unapplied_amount || 0);
            const nextDraft: Record<number, string> = {};
            (invoices || []).forEach((invoice) => {
                if (remaining <= 0) return;
                const apply = Math.min(Number(invoice.outstanding_amount || 0), remaining);
                if (apply > 0) {
                    nextDraft[invoice.invoice_id] = apply.toFixed(2);
                    remaining = Number((remaining - apply).toFixed(2));
                }
            });
            setAllocationDraft(nextDraft);
            if (searchParams.get('allocate') !== '1') {
                const next = new URLSearchParams(searchParams);
                next.set('allocate', '1');
                setSearchParams(next, { replace: true });
            }
            if ((invoices || []).length === 0) {
                toast.info('No open invoices found for this customer.');
            }
        } catch (error: any) {
            toast.error(error?.message || 'Failed to load open invoices for allocation');
        } finally {
            setAllocationLoading(false);
        }
    };

    const closeAllocation = () => {
        setAllocating(false);
        setOpenInvoices([]);
        setAllocationDraft({});
        const next = new URLSearchParams(searchParams);
        next.delete('allocate');
        setSearchParams(next, { replace: true });
    };

    const autoAllocate = () => {
        let remaining = Number(receipt?.unapplied_amount || 0);
        if (!Number.isFinite(remaining) || remaining <= 0) return;
        const nextDraft: Record<number, string> = {};
        openInvoices.forEach((invoice) => {
            if (remaining <= 0) return;
            const apply = Math.min(Number(invoice.outstanding_amount || 0), remaining);
            if (apply > 0) {
                nextDraft[invoice.invoice_id] = apply.toFixed(2);
                remaining = Number((remaining - apply).toFixed(2));
            }
        });
        setAllocationDraft(nextDraft);
    };

    const submitAllocation = async () => {
        if (!receipt) return;
        if (pendingAllocationLines.length === 0) {
            toast.error('Enter at least one invoice allocation amount.');
            return;
        }
        if (pendingAllocationTotal > Number(receipt.unapplied_amount || 0) + 0.01) {
            toast.error(`Allocation exceeds unapplied balance (${formatAmount(receipt.unapplied_amount)}).`);
            return;
        }
        setAllocationSaving(true);
        try {
            const updated = await api.financials.allocateArReceipt(Number(receipt.receipt_id), pendingAllocationLines);
            setReceipt(updated);
            setAllocationDraft({});
            toast.success(`Allocated AED ${formatAmount(pendingAllocationTotal)} from ${formatReceiptNumber(updated.receipt_id)}.`);
            if (Number(updated.unapplied_amount || 0) <= 0.01) {
                closeAllocation();
                toast.info('Prepayment fully allocated.');
            } else {
                await beginAllocation();
            }
        } catch (error: any) {
            toast.error(error?.message || 'Failed to allocate receipt');
        } finally {
            setAllocationSaving(false);
        }
    };

    if (loading) {
        return <div className="p-8 text-sm text-slate-600">Loading receipt...</div>;
    }

    if (!receipt) {
        return (
            <div className="p-8 space-y-4">
                <div className="text-sm text-slate-600">Receipt not found.</div>
                <Button variant="outline" onClick={() => navigate('/financials')}>
                    <ArrowLeft className="mr-2 h-4 w-4" /> Back to Financials
                </Button>
            </div>
        );
    }

    const primaryReference = receipt.invoice_hint_id
        ? formatInvoiceNumber(receipt.invoice_hint_id)
        : (receipt.allocations || []).length === 1
            ? formatInvoiceNumber(receipt.allocations[0].document_id)
            : receipt.so_id
                ? formatSoNumber(receipt.so_id)
                : 'Customer Account Receipt';

    return (
        <div className="space-y-6 p-6">
            <SendPdfModal
                open={showSendPdfModal}
                onOpenChange={setShowSendPdfModal}
                documentLabel="Receipt"
                documentNumber={documentNumber}
                attachmentName={`${documentNumber.replace(/\//g, '_')}.pdf`}
                defaultTo={customer?.email_address || ''}
                defaultSubject={`${receiptTitle(receipt.mode)} ${documentNumber}`}
                defaultBody={`Dear Customer,\n\nPlease find attached ${receiptTitle(receipt.mode).toLowerCase()} ${documentNumber}.\n\nRegards,\nHADAF COMPOSITES`}
                onSend={handleSendPdf}
            />

            <div className="flex flex-col gap-3 rounded-lg border bg-white p-4 shadow-sm lg:flex-row lg:items-center lg:justify-between">
                <div>
                    <div className="text-sm text-slate-500">{receiptTitle(receipt.mode)}</div>
                    <h1 className="text-2xl font-semibold text-slate-900">{documentNumber}</h1>
                    <p className="text-sm text-slate-600">
                        {customer?.customer_name || `Customer #${receipt.customer_id}`} | {formatDate(receipt.receipt_date)}
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={() => navigate('/financials')}>
                        <ArrowLeft className="mr-2 h-4 w-4" /> Back
                    </Button>
                    {canPrint && (
                        <>
                            <Button variant="outline" onClick={handlePrint}>
                                <Printer className="mr-2 h-4 w-4" /> Preview
                            </Button>
                            <Button variant="outline" onClick={handleDownloadPdf}>
                                <Download className="mr-2 h-4 w-4" /> PDF
                            </Button>
                        </>
                    )}
                    {canAllocate && (
                        <Button variant="outline" onClick={beginAllocation} disabled={allocationLoading}>
                            <Link2 className="mr-2 h-4 w-4" /> {allocationLoading ? 'Loading...' : 'Allocate to Invoice'}
                        </Button>
                    )}
                    {canEmail && (
                        <Button variant="outline" onClick={() => setShowSendPdfModal(true)}>
                            <Send className="mr-2 h-4 w-4" /> Send
                        </Button>
                    )}
                    {canShare && (
                        <>
                            <Button variant="outline" onClick={handleCopyShareLink}>
                                <Link2 className="mr-2 h-4 w-4" /> Share Link
                            </Button>
                            <Button variant="outline" onClick={handleShareWhatsApp}>
                                <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp
                            </Button>
                        </>
                    )}
                </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
                <Card>
                    <CardHeader>
                        <CardTitle>Receipt Overview</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="rounded-md border bg-slate-50 p-4">
                                <div className="text-xs uppercase tracking-wide text-slate-500">Amount Received</div>
                                <div className="mt-1 text-2xl font-bold text-slate-900">AED {formatAmount(receipt.amount)}</div>
                            </div>
                            <div className="rounded-md border bg-slate-50 p-4">
                                <div className="text-xs uppercase tracking-wide text-slate-500">Mode</div>
                                <div className="mt-1 text-lg font-semibold text-slate-900">{receiptModeLabel(receipt.mode)}</div>
                            </div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                            <div>
                                <div className="text-xs uppercase tracking-wide text-slate-500">Against</div>
                                <div className="mt-1 font-medium text-slate-900">{primaryReference}</div>
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wide text-slate-500">Payment Reference</div>
                                <div className="mt-1 font-medium text-slate-900">{receipt.reference_no || '-'}</div>
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wide text-slate-500">Applied Amount</div>
                                <div className="mt-1 font-medium text-slate-900">AED {formatAmount(receipt.applied_amount)}</div>
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wide text-slate-500">Unapplied Amount</div>
                                <div className="mt-1 font-medium text-slate-900">AED {formatAmount(receipt.unapplied_amount)}</div>
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wide text-slate-500">Journal ID</div>
                                <div className="mt-1 font-medium text-slate-900">{receipt.journal_id || '-'}</div>
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wide text-slate-500">Receipt Date</div>
                                <div className="mt-1 font-medium text-slate-900">{formatDate(receipt.receipt_date)}</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>References</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                        <div>
                            <div className="text-xs uppercase tracking-wide text-slate-500">Customer</div>
                            <div className="mt-1 font-medium text-slate-900">{customer?.customer_name || `Customer #${receipt.customer_id}`}</div>
                            {customer?.email_address && <div className="text-slate-600">{customer.email_address}</div>}
                            {customer?.poc_contact && <div className="text-slate-600">{customer.poc_contact}</div>}
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-wide text-slate-500">Sale Order</div>
                            <div className="mt-1 font-medium text-slate-900">{receipt.so_id ? formatSoNumber(receipt.so_id) : '-'}</div>
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-wide text-slate-500">Estimate</div>
                            <div className="mt-1 font-medium text-slate-900">{saleOrder?.estimate_ref || '-'}</div>
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-wide text-slate-500">Customer PO / LPO</div>
                            <div className="mt-1 font-medium text-slate-900">{saleOrder?.customer_po_no || '-'}</div>
                        </div>
                        {canShare && (
                            <div className="rounded-md border bg-slate-50 p-3">
                                <div className="text-xs uppercase tracking-wide text-slate-500">Share Link</div>
                                <div className="mt-2 break-all text-sm text-slate-700">{shareUrl || 'No active link generated in this session.'}</div>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    <Button variant="outline" size="sm" onClick={handleCopyShareLink}>
                                        <Copy className="mr-2 h-4 w-4" /> Copy Link
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={handleRevokeShareLink}>
                                        Revoke Link
                                    </Button>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {(allocating || allocationLoading) && canAllocate && (
                <Card>
                    <CardHeader>
                        <CardTitle>Allocate Advance to Invoice</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {allocationLoading ? (
                            <div className="text-sm text-slate-500">Loading open invoices...</div>
                        ) : (
                            <>
                                <div className="grid gap-4 md:grid-cols-3">
                                    <div className="rounded-md border bg-slate-50 p-3 text-sm">
                                        Receipt Amount
                                        <div className="mt-1 font-semibold text-slate-900">AED {formatAmount(receipt.amount)}</div>
                                    </div>
                                    <div className="rounded-md border bg-slate-50 p-3 text-sm">
                                        Unapplied Balance
                                        <div className="mt-1 font-semibold text-slate-900">AED {formatAmount(receipt.unapplied_amount)}</div>
                                    </div>
                                    <div className="rounded-md border bg-slate-50 p-3 text-sm">
                                        Allocating Now
                                        <div className="mt-1 font-semibold text-slate-900">AED {formatAmount(pendingAllocationTotal)}</div>
                                    </div>
                                </div>

                                {openInvoices.length === 0 ? (
                                    <div className="text-sm text-slate-500">No open invoices found for this customer.</div>
                                ) : (
                                    <div className="overflow-x-auto rounded-md border">
                                        <table className="w-full text-sm">
                                            <thead className="bg-slate-50">
                                                <tr>
                                                    <th className="p-3 text-left font-medium text-slate-600">Invoice</th>
                                                    <th className="p-3 text-right font-medium text-slate-600">Outstanding</th>
                                                    <th className="p-3 text-right font-medium text-slate-600">Apply</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {openInvoices.map((invoice) => (
                                                    <tr key={invoice.invoice_id} className="border-t">
                                                        <td className="p-3">
                                                            <Link className="font-medium text-blue-700 hover:underline" to={`/legacy/invoices/${invoice.invoice_id}`}>
                                                                {invoice.invoice_no}
                                                            </Link>
                                                        </td>
                                                        <td className="p-3 text-right text-slate-700">AED {formatAmount(invoice.outstanding_amount)}</td>
                                                        <td className="p-3">
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                max={invoice.outstanding_amount}
                                                                className="w-full rounded border p-2 text-right"
                                                                value={allocationDraft[invoice.invoice_id] ?? ''}
                                                                onChange={(event) =>
                                                                    setAllocationDraft((prev) => ({
                                                                        ...prev,
                                                                        [invoice.invoice_id]: event.target.value,
                                                                    }))
                                                                }
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                <div className="flex flex-wrap gap-2">
                                    <Button variant="outline" onClick={autoAllocate} disabled={allocationSaving || openInvoices.length === 0}>
                                        Auto Allocate
                                    </Button>
                                    <Button onClick={submitAllocation} disabled={allocationSaving || pendingAllocationLines.length === 0}>
                                        {allocationSaving ? 'Allocating...' : 'Allocate to Invoice'}
                                    </Button>
                                    <Button variant="outline" onClick={closeAllocation} disabled={allocationSaving}>
                                        Close
                                    </Button>
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>
            )}

            <Card>
                <CardHeader>
                    <CardTitle>Allocations</CardTitle>
                </CardHeader>
                <CardContent>
                    {(receipt.allocations || []).length === 0 ? (
                        <div className="text-sm text-slate-500">
                            No invoice allocations are recorded yet.
                            {String(receipt.mode || '').toUpperCase() === 'CUSTOMER_PREPAYMENT' && ' This advance remains available for later adjustment.'}
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-md border">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50">
                                    <tr>
                                        <th className="p-3 text-left font-medium text-slate-600">Invoice</th>
                                        <th className="p-3 text-left font-medium text-slate-600">Allocated At</th>
                                        <th className="p-3 text-right font-medium text-slate-600">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {receipt.allocations.map((line) => (
                                        <tr key={line.allocation_id} className="border-t">
                                            <td className="p-3 font-medium text-slate-900">
                                                <Link className="text-blue-700 hover:underline" to={`/legacy/invoices/${line.document_id}`}>
                                                    {formatInvoiceNumber(line.document_id)}
                                                </Link>
                                            </td>
                                            <td className="p-3 text-slate-600">{formatDate(line.allocated_at || '')}</td>
                                            <td className="p-3 text-right font-medium text-slate-900">AED {formatAmount(line.apply_amount)}</td>
                                        </tr>
                                    ))}
                                    <tr className="border-t bg-slate-50">
                                        <td className="p-3 font-semibold text-slate-900" colSpan={2}>Total Allocated</td>
                                        <td className="p-3 text-right font-semibold text-slate-900">AED {formatAmount(allocationTotal)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
